import { Link, createFileRoute, notFound } from '@tanstack/react-router'
import 'highlight.js/styles/github-dark.min.css'
import { ArrowLeft } from 'lucide-react'
import Reveal from '../../../components/preview/Reveal'
import { getPostPageData } from '@/lib/postServerFns'

export const Route = createFileRoute('/blog/$category/$slug')({
  loader: async ({ params: { category, slug } }) => {
    const data = await getPostPageData({ data: { category, slug } })
    if (!data) throw notFound()
    return data
  },
  component: BlogPost,
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: '404 — Article Not Found | Suriyaprakash Somu' },
          { name: 'robots', content: 'noindex, follow' },
          {
            name: 'description',
            content: 'The requested blog article does not exist.',
          },
          { property: 'og:title', content: '404 — Article Not Found' },
          {
            property: 'og:description',
            content: 'The requested blog article does not exist.',
          },
          { name: 'twitter:title', content: '404 — Article Not Found' },
          {
            name: 'twitter:description',
            content: 'The requested blog article does not exist.',
          },
        ],
      }
    }

    const { post, category: cat } = loaderData
    const { frontmatter } = post

    // seoTitle is a concise search title; the full editorial title stays on-page.
    // frontmatter.title stays shorter for on-page H1 and social cards.
    const pageTitle = frontmatter.seoTitle ?? frontmatter.title
    const keywords =
      frontmatter.keywords.length > 0
        ? frontmatter.keywords
        : [...frontmatter.tags, cat.name]
    const postUrl = `https://www.suriyaprakash.in/blog/${post.category}/${post.slug}`
    const ogImage =
      (frontmatter.coverImage
        ? new URL(
            frontmatter.coverImage,
            'https://www.suriyaprakash.in/',
          ).toString()
        : undefined) ?? `https://www.suriyaprakash.in/og/${post.slug}.png`
    const { wordCount, readingTime: readingMinutes } = loaderData
    const title = `${pageTitle} | Suriya`
    const aiSummary = `${frontmatter.description} This ${cat.name} article by Suriyaprakash Somu covers ${keywords.join(', ')} and takes about ${readingMinutes} ${readingMinutes === 1 ? 'minute' : 'minutes'} to read.`

    const jsonLd: Array<Record<string, unknown>> = [
      {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': postUrl,
        },
        headline: frontmatter.title,
        description: frontmatter.description,
        datePublished: frontmatter.date,
        dateModified: frontmatter.updated ?? frontmatter.date,
        image: ogImage,
        author: {
          '@type': 'Person',
          '@id': 'https://www.suriyaprakash.in/#person',
          name: 'Suriyaprakash Somu',
          url: 'https://www.suriyaprakash.in',
        },
        publisher: {
          '@type': 'Person',
          '@id': 'https://www.suriyaprakash.in/#person',
          name: 'Suriyaprakash Somu',
          url: 'https://www.suriyaprakash.in',
        },
        url: postUrl,
        isPartOf: {
          '@type': 'Blog',
          name: 'Suriyaprakash Somu Blog',
          url: 'https://www.suriyaprakash.in/blog',
        },
        articleSection: cat.name,
        wordCount,
        timeRequired: `PT${readingMinutes}M`,
        keywords: keywords.join(', '),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: 'https://www.suriyaprakash.in',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Blog',
            item: 'https://www.suriyaprakash.in/blog',
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: cat.name,
            item: `https://www.suriyaprakash.in/blog/${cat.slug}`,
          },
          {
            '@type': 'ListItem',
            position: 4,
            name: frontmatter.title,
            item: postUrl,
          },
        ],
      },
    ]

    if (post.faq.length > 0) {
      jsonLd.push({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: post.faq.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      })
    }

    return {
      meta: [
        { title },
        { name: 'title', content: title },
        { name: 'description', content: frontmatter.description },
        { name: 'robots', content: 'index, follow, max-image-preview:large' },
        { name: 'keywords', content: keywords.join(', ') },
        { name: 'author', content: 'Suriyaprakash Somu' },
        { name: 'geo.region', content: 'IN-TN' },
        {
          name: 'geo.placename',
          content: 'Erode, Tamil Nadu, India',
        },
        { name: 'ai-summary', content: aiSummary },
        { property: 'og:title', content: frontmatter.title },
        { property: 'og:description', content: frontmatter.description },
        { property: 'og:type', content: 'article' },
        { property: 'og:image', content: ogImage },
        ...(!frontmatter.coverImage
          ? [
              { property: 'og:image:width', content: '1200' },
              { property: 'og:image:height', content: '630' },
            ]
          : []),
        {
          property: 'og:image:alt',
          content: `${frontmatter.title} — article by Suriyaprakash Somu`,
        },
        { property: 'og:url', content: postUrl },
        {
          property: 'article:published_time',
          content: frontmatter.date,
        },
        {
          property: 'article:modified_time',
          content: frontmatter.updated ?? frontmatter.date,
        },
        {
          property: 'article:author',
          content: 'Suriyaprakash Somu',
        },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: frontmatter.title },
        { name: 'twitter:description', content: frontmatter.description },
        { name: 'twitter:image', content: ogImage },
        {
          name: 'twitter:image:alt',
          content: `${frontmatter.title} — article by Suriyaprakash Somu`,
        },
      ],
      links: [
        {
          rel: 'canonical',
          href: postUrl,
        },
        {
          rel: 'alternate',
          type: 'application/rss+xml',
          title: 'RSS',
          href: 'https://www.suriyaprakash.in/rss.xml',
        },
      ],
      scripts: jsonLd.map((data) => ({
        type: 'application/ld+json',
        children: JSON.stringify(data).replace(/</g, '\\u003c'),
      })),
    }
  },
})

function BlogPost() {
  const {
    post,
    category: cat,
    seriesPosts,
    seriesIndex,
    contentHtml,
    readingTime,
  } = Route.useLoaderData()
  const { frontmatter } = post

  return (
    <article className="min-h-screen bg-cream-50">
      <header className="relative overflow-hidden bg-forest-strong pb-14 pt-28 text-cream-50 sm:pb-20 sm:pt-36 lg:pb-24">
        <div
          className="preview-blueprint absolute inset-0"
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 bg-[radial-gradient(circle_at_78%_18%,rgba(172,196,173,0.18),transparent_36rem)]"
          aria-hidden="true"
        />
        <span
          className="preview-cross left-6 top-28 hidden sm:block"
          aria-hidden="true"
        />
        <span
          className="preview-cross right-6 top-28 hidden sm:block"
          aria-hidden="true"
        />

        <div className="container-custom relative">
          <Link
            to="/blog/$category"
            params={{ category: post.category }}
            className="preview-link-sweep preview-mono focus-ring mb-10 inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-cream-50/85 transition-colors hover:text-sage-200"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {cat.name}
          </Link>

          <p className="preview-mono flex items-center justify-between border-b border-white/15 pb-5 text-[10px] font-bold uppercase tracking-[0.24em] text-sage-200">
            <span>Field note / {cat.name}</span>
            <span>+</span>
          </p>

          <h1 className="mt-8 max-w-6xl break-words font-heading text-[clamp(3.25rem,8vw,7.5rem)] leading-[0.88] tracking-[-0.045em] text-white">
            {frontmatter.title}
          </h1>

          <p className="mt-7 max-w-3xl text-base leading-7 text-cream-50/85 sm:text-lg sm:leading-8">
            {frontmatter.description}
          </p>

          <div className="preview-mono mt-10 grid border-y border-white/15 text-[10px] font-bold uppercase tracking-[0.18em] text-cream-50/85 sm:grid-cols-3">
            <div className="border-b border-white/15 py-4 sm:border-b-0 sm:border-r sm:pr-6">
              <span className="block text-sage-200">Published</span>
              <time
                dateTime={frontmatter.date}
                className="mt-1 block text-cream-50"
              >
                {new Date(frontmatter.date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </time>
            </div>
            <div className="border-b border-white/15 py-4 sm:border-b-0 sm:border-r sm:px-6">
              <span className="block text-sage-200">Reading time</span>
              <span className="mt-1 block text-cream-50">
                {readingTime} min read
              </span>
            </div>
            <div className="py-4 sm:pl-6">
              <span className="block text-sage-200">
                {frontmatter.updated && frontmatter.updated !== frontmatter.date
                  ? 'Updated'
                  : 'Filed under'}
              </span>
              <span className="mt-1 block text-cream-50">
                {frontmatter.updated && frontmatter.updated !== frontmatter.date
                  ? new Date(frontmatter.updated).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })
                  : cat.name}
              </span>
            </div>
          </div>

          {frontmatter.tags.length > 0 && (
            <div className="preview-mono mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-[10px] font-bold uppercase tracking-[0.16em]">
              <span className="text-sage-200">Topics</span>
              {frontmatter.tags.map((tag: string) => (
                <Link
                  key={tag}
                  to="/blog/tag/$tag"
                  params={{ tag }}
                  className="focus-ring inline-flex min-h-8 min-w-8 items-center text-cream-50/85 underline decoration-white/20 underline-offset-4 transition-colors hover:text-sage-200 hover:decoration-sage-200"
                >
                  {tag}
                </Link>
              ))}
            </div>
          )}
        </div>
      </header>

      <div className="container-custom grid gap-x-12 pb-16 pt-10 sm:pb-24 sm:pt-16 xl:grid-cols-[minmax(0,1fr)_16rem] xl:items-start xl:gap-x-16 xl:pt-20 2xl:gap-x-20">
        {post.toc.filter((t) => t.depth === 2).length > 3 && (
          <aside className="mb-12 xl:sticky xl:top-28 xl:col-start-2 xl:row-start-1 xl:mb-0">
            <Reveal>
              <nav
                aria-label="Table of contents"
                className="border-y border-line py-5"
              >
                <p className="preview-mono flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.2em] text-forest">
                  <span>On this page</span>
                  <span>+</span>
                </p>
                <ol className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
                  {post.toc
                    .filter((t) => t.depth === 2)
                    .map((t, index) => (
                      <li key={t.id} className="flex gap-3">
                        <span className="preview-mono pt-0.5 text-[9px] text-muted">
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        <a
                          href={`#${t.id}`}
                          className="text-sm leading-5 text-ink-soft underline decoration-transparent underline-offset-4 transition-colors hover:text-forest hover:decoration-forest/40"
                        >
                          {t.text}
                        </a>
                      </li>
                    ))}
                </ol>
              </nav>
            </Reveal>
          </aside>
        )}

        <div className="min-w-0 xl:col-start-1 xl:row-start-1">
          <div
            className="prose-blog"
            dangerouslySetInnerHTML={{ __html: contentHtml }}
          />

          {seriesPosts.length > 1 && seriesIndex >= 0 && (
            <Reveal className="mt-14 sm:mt-20">
              <section className="border-y border-line py-7 sm:py-9">
                <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
                  <div>
                    <p className="preview-mono text-[10px] font-bold uppercase tracking-[0.2em] text-forest">
                      Series / {post.frontmatter.series}
                    </p>
                    <h2 className="mt-3 font-heading text-3xl leading-none tracking-[-0.03em] text-ink sm:text-4xl">
                      Continue the sequence.
                    </h2>
                  </div>
                  <p className="preview-mono shrink-0 text-[10px] font-bold uppercase tracking-[0.16em] text-muted">
                    Part {seriesIndex + 1} / {seriesPosts.length}
                  </p>
                </div>
                <div className="mt-7 border-t border-line">
                  {seriesPosts.map(
                    (
                      sp: { slug: string; category: string; title: string },
                      i: number,
                    ) => {
                      const isCurrent = i === seriesIndex
                      return (
                        <div
                          key={sp.slug}
                          className={`grid grid-cols-[2rem_minmax(0,1fr)_auto] items-center gap-3 border-b border-line py-4 text-sm ${
                            isCurrent ? 'text-forest' : 'text-ink-soft'
                          }`}
                        >
                          <span className="preview-mono text-[10px] font-bold text-muted">
                            {String(i + 1).padStart(2, '0')}
                          </span>
                          {isCurrent ? (
                            <span className="font-semibold">{sp.title}</span>
                          ) : (
                            <Link
                              to="/blog/$category/$slug"
                              params={{
                                category: sp.category,
                                slug: sp.slug,
                              }}
                              className="font-medium transition-colors hover:text-forest"
                            >
                              {sp.title}
                            </Link>
                          )}
                          {isCurrent && (
                            <span className="preview-mono text-[9px] font-bold uppercase tracking-[0.14em] text-muted">
                              Reading
                            </span>
                          )}
                        </div>
                      )
                    },
                  )}
                </div>
              </section>
            </Reveal>
          )}

          <footer className="mt-14 border-y border-line py-7 sm:mt-20 sm:py-9">
            <Reveal>
              <div className="grid gap-5 sm:grid-cols-[10rem_minmax(0,1fr)] sm:gap-8">
                <p className="preview-mono text-[10px] font-bold uppercase tracking-[0.2em] text-forest">
                  About the author
                </p>
                <p className="text-sm leading-7 text-ink-soft sm:text-base sm:leading-8">
                  Suriyaprakash Somu is a full-stack developer based in Erode,
                  Tamil Nadu, building React and Node.js applications on
                  relational databases. He
                  works on customer-facing experiences, reusable interfaces and
                  access-controlled backend workflows.{' '}
                  <Link
                    to="/work"
                    className="focus-ring font-semibold text-forest underline underline-offset-4"
                  >
                    Explore his development work
                  </Link>{' '}
                  or{' '}
                  <a
                    href="/#p-contact"
                    className="focus-ring font-semibold text-forest underline underline-offset-4"
                  >
                    discuss a project or developer role
                  </a>
                  .
                </p>
              </div>
            </Reveal>
          </footer>
        </div>
      </div>
    </article>
  )
}
